import type {
  ILinkData,
  IMarketingLink,
  IQuickLink,
  IQuickLinkAnalyticsTags,
  IQuickLinkRedirects,
  ISocialMediaTags,
  QrCodeSettings,
} from '../../../../../shared/types';
import { RedirectsClientOverride } from '../../../../../shared/types';
import { Redirect as RedirectEnum } from '../../screens/ScreenLinkHubQuickLinkCreator/steps/RedirectsStep/Redirects.enums';
import type { Redirect as RedirectType } from '../../screens/ScreenLinkHubQuickLinkCreator/steps/RedirectsStep/Redirects.types';
import { LinkWebOnlyLinkType } from '../../types/linkTypes';
import { LinkDataService } from '../LinkDataService';

export class QuickLinkEditorService {
  private static getLinkId = (marketingLink: IMarketingLink): string | undefined => {
    return marketingLink?.id;
  };

  private static getTemplateId = (marketingLink: IMarketingLink): string | undefined => {
    return marketingLink?.data?.['~quick_link_template_id'];
  };

  private static extractDomain(url: string): string {
    const parsedUrl = new URL(url);

    return parsedUrl.hostname;
  }

  private static extractAliasFromUrl(url: string): string | undefined {
    const urlObject = new URL(url);

    const pathSegments = urlObject.pathname.split('/');

    const alias = pathSegments.pop();

    return alias;
  }

  private static getDomain = (marketingLink: IMarketingLink): string => {
    const domain = marketingLink?.domain_alias?.domain;

    if (domain?.includes('/e')) {
      return domain.replace('/e', '');
    }

    if (marketingLink?.url) {
      return this.extractDomain(marketingLink.url);
    }

    return domain ?? '';
  };

  private static getTitle = (marketingLink: IMarketingLink): string | undefined => {
    return marketingLink?.data?.$marketing_title;
  };

  private static getAlias = (marketingLink: IMarketingLink): string | undefined => {
    if (marketingLink?.domain_alias?.alias) {
      return marketingLink.domain_alias.alias;
    }

    if (marketingLink?.url) {
      return this.extractAliasFromUrl(marketingLink.url);
    }

    return undefined;
  };

  private static getAnalyticsTags = (marketingLink: IMarketingLink): IQuickLinkAnalyticsTags => {
    const analyticsTags = new Object() as IQuickLinkAnalyticsTags;

    if (marketingLink?.feature) {
      analyticsTags.feature = marketingLink?.feature;
    }

    if (marketingLink?.channel) {
      analyticsTags.channel = marketingLink?.channel;
    }

    if (marketingLink?.campaign) {
      analyticsTags.campaign = marketingLink?.campaign;
    }

    if (marketingLink?.tags) {
      analyticsTags.tags = marketingLink?.tags;
    }

    return analyticsTags;
  };

  private static getRedirectValidType = (redirect: RedirectType): RedirectsClientOverride => {
    if (redirect === RedirectEnum.Deepview) {
      return RedirectsClientOverride.Deepview;
    } else if (redirect === RedirectEnum.Web) {
      return RedirectsClientOverride.WebUrl;
    } else {
      return RedirectsClientOverride.DefaultRedirect;
    }
  };

  private static getRedirects = (marketingLink: IMarketingLink): IQuickLinkRedirects => {
    const redirects: IQuickLinkRedirects = new Object();

    if (marketingLink?.data?.$ios_url) {
      redirects.ios = {
        override: this.getRedirectValidType('web'),
        value: marketingLink?.data?.$ios_url,
      };
    } else if (marketingLink?.data?.$ios_deepview) {
      redirects.ios = {
        override: this.getRedirectValidType('deepview'),
        value: marketingLink?.data?.$ios_deepview,
      };
    }

    if (marketingLink?.data?.$android_url) {
      redirects.android = {
        override: this.getRedirectValidType('web'),
        value: marketingLink?.data?.$android_url,
      };
    } else if (marketingLink?.data?.$android_deepview) {
      redirects.android = {
        override: this.getRedirectValidType('deepview'),
        value: marketingLink?.data?.$android_deepview,
      };
    }

    if (marketingLink?.data?.$desktop_url) {
      redirects.desktop = {
        override: this.getRedirectValidType('web'),
        value: marketingLink?.data?.$desktop_url,
      };
    } else if (marketingLink?.data?.$desktop_deepview) {
      redirects.desktop = {
        override: this.getRedirectValidType('deepview'),
        value: marketingLink?.data?.$desktop_deepview,
      };
    }

    let webOnly = marketingLink?.data?.$web_only as any;
    if (webOnly) {
      webOnly = typeof webOnly === 'string' ? webOnly.toLowerCase() : webOnly;
      redirects.isWebOnly = webOnly === LinkWebOnlyLinkType.TRUE || webOnly === true;
    } else {
      redirects.isWebOnly = false;
    }

    return redirects;
  };

  private static getLinkData = (marketingLink: IMarketingLink): ILinkData => {
    const linkDataWithoutPresets: Record<string, any> = LinkDataService.excludePresetsFromLinkCreatorData(
      marketingLink?.data,
      'quick'
    );

    return linkDataWithoutPresets;
  };

  private static getOriginalWebUrl = (marketingLink: Record<string, any>): string | undefined => {
    // First try to use $canonical_url as it's less likely to be changed after link creation
    if (marketingLink?.data?.$canonical_url) {
      return marketingLink.data.$canonical_url;
    }

    // Fallback to $desktop_url if $canonical_url is not available
    if (marketingLink?.data?.$desktop_url) {
      return marketingLink.data.$desktop_url;
    }

    return undefined;
  };

  private static getSocialMediaTags = (marketingLink: IMarketingLink): ISocialMediaTags => {
    const socialMediaTags: ISocialMediaTags = new Object();

    if (marketingLink?.data?.$og_title) {
      socialMediaTags.title = marketingLink?.data?.$og_title;
    }

    if (marketingLink?.data?.$og_description) {
      socialMediaTags.description = marketingLink?.data?.$og_description;
    }

    if (marketingLink?.data?.$og_image_url) {
      socialMediaTags.imageUrl = marketingLink?.data?.$og_image_url;
    }

    return socialMediaTags;
  };

  private static getQrCodeSettings = (marketingLink: IMarketingLink): QrCodeSettings => {
    return marketingLink?.qr_code_settings || {};
  };

  public static getQuickLinkPayload = (marketingLink: IMarketingLink): IQuickLink => {
    return {
      id: this.getLinkId(marketingLink),
      templateId: this.getTemplateId(marketingLink),
      domain: this.getDomain(marketingLink),
      title: this.getTitle(marketingLink) as string,
      alias: this.getAlias(marketingLink),
      analyticsTags: this.getAnalyticsTags(marketingLink),
      redirects: this.getRedirects(marketingLink),
      linkData: this.getLinkData(marketingLink),
      socialMediaTags: this.getSocialMediaTags(marketingLink),
      qrCodeSettings: this.getQrCodeSettings(marketingLink),
      originalWebUrl: this.getOriginalWebUrl(marketingLink),
    };
  };
}
